import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button as ButtonComponent } from '@/components/ui/button'
import { CubeLoader as CubeLoaderComponent } from '@/components/ui/cube-loader'
import { cn } from '@/lib/utils'
import { ButtonProps, CubeLoaderProps } from '@/types'
import {
    AD_HISTORY_FORBIDDEN_MESSAGE,
    AD_HISTORY_OFF_MESSAGE,
    AD_HISTORY_UNAVAILABLE_MESSAGE,
    REFRESH_LIMIT_HOURS,
    STALE_NOTE,
    copyLinesOf,
    dropReasonText,
    formatConfidence,
    formatMoment,
    groupStatusNote,
    notOfferedText,
    otherReasonTexts,
    resultsLine,
    winnerStateLabel,
    winnersStateNote,
} from './adHistoryUtils'
import { useAdHistory } from './useAdHistory'
import type { AdHistoryController } from './useAdHistory'
import type { AdHistoryGroupStatus, AdHistoryOther, AdHistoryWinner, GenerationBasisPurpose } from './types'

// Type casts for the UI components from JSX files
const Button = ButtonComponent as React.FC<
    ButtonProps & Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'aria-label' | 'aria-pressed'>
>
const CubeLoader = CubeLoaderComponent as React.FC<CubeLoaderProps>

const RECONNECT_LABEL = 'Reconnect Meta'

const PURPOSES: { value: GenerationBasisPurpose; label: string }[] = [
    { value: 'PERSONAS', label: 'Audience personas' },
    { value: 'CREATIVE_PROMPTS', label: 'Image and video prompts' },
]

const MODE_TEXT = {
    manual: 'Manual: only the winning copy you approve is used.',
    automated: 'Automated: every winning copy that qualifies is used, except what you exclude.',
} as const

const TONE_CLASS = {
    ok: 'tw:text-gray-200',
    warning: 'tw:text-yellow-300',
    error: 'tw:text-red-300',
} as const

interface GroupLineProps {
    group: AdHistoryGroupStatus
    showAccount: boolean
    campaignId: string | null
}

const GroupLine = ({ group, showAccount, campaignId }: GroupLineProps) => {
    const note = groupStatusNote(group)
    const settingsPath = `/campaigns/media-buying/${campaignId}/settings/ad-account`
    return (
        <li
            className={cn('tw:text-sm', TONE_CLASS[note.tone])}
            data-testid={`ad-history-group-${group.adAccount}`}
            role={note.tone === 'ok' ? undefined : 'status'}
        >
            {showAccount && <span className="tw:text-gray-200">Ad account {group.adAccount}: </span>}
            {note.reconnect && note.text === RECONNECT_LABEL ? (
                <Link to={settingsPath} className="tw:underline">
                    {note.text}
                </Link>
            ) : (
                <>
                    {note.text}
                    {note.reconnect && (
                        <>
                            {' '}
                            <Link to={settingsPath} className="tw:underline">
                                {RECONNECT_LABEL}
                            </Link>
                        </>
                    )}
                </>
            )}
        </li>
    )
}

const StatusSection = ({ controller }: { controller: AdHistoryController }) => {
    const { status, refresh, refreshing, campaignId } = controller
    if (!status) return null
    const waiting = formatMoment(status.nextRefreshAllowedAt)
    const refreshBlocked = refreshing || status.refreshPending || !!waiting
    return (
        <section className="tw:flex tw:flex-col tw:gap-2" aria-label="Status of the results read">
            <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2">
                <span className="tw:text-sm tw:font-semibold">Results from Meta</span>
                <Button type="button" size="sm" variant="secondary" disabled={refreshBlocked} onClick={() => refresh()}>
                    Refresh now
                </Button>
            </div>
            {status.groups.length === 0 ? (
                <p className="tw:m-0 tw:text-sm tw:text-gray-200">
                    No results have been read yet. They are read daily once a Meta ad account with ads is connected.
                </p>
            ) : (
                <ul className="tw:m-0 tw:flex tw:list-none tw:flex-col tw:gap-1 tw:!p-0">
                    {status.groups.map((group) => (
                        <GroupLine
                            key={group.adAccount}
                            group={group}
                            showAccount={status.groups.length > 1}
                            campaignId={campaignId}
                        />
                    ))}
                </ul>
            )}
            {status.stale && (
                <p className="tw:m-0 tw:text-sm tw:text-yellow-300" role="status">
                    {STALE_NOTE}
                </p>
            )}
            {status.refreshPending && (
                <p className="tw:m-0 tw:text-xs tw:text-gray-200">A refresh is waiting for the next hourly read.</p>
            )}
            {!status.refreshPending && waiting && (
                <p className="tw:m-0 tw:text-xs tw:text-gray-200">
                    Refreshing is limited to once every {REFRESH_LIMIT_HOURS} hours; you can refresh again after{' '}
                    {waiting}.
                </p>
            )}
        </section>
    )
}

interface WinnerRowProps {
    winner: AdHistoryWinner
    controller: AdHistoryController
    mode: string
    stateNote: Pick<NonNullable<AdHistoryController['winners']>, 'state' | 'reason'>
}

const WinnerRow = ({ winner, controller, mode, stateNote }: WinnerRowProps) => {
    const { decide, changing } = controller
    const lines = copyLinesOf(winner)
    const label = lines.join(' ')
    const confidence = formatConfidence(winner.pBest)
    const stateLabel = winnerStateLabel(winner, mode)
    const dropped = winner.state === 'dropped'
    const notOffered = winner.state === 'not_offered'
    const key = winner.key
    return (
        <li
            className="tw:flex tw:flex-wrap tw:items-start tw:justify-between tw:gap-2 tw:px-3 tw:py-2"
            data-testid={`ad-history-winner-${key ?? 'dropped'}`}
        >
            <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-0.5">
                {lines.map((line, index) => (
                    <span key={index} className="tw:text-sm tw:break-words">
                        {line}
                    </span>
                ))}
                <span className="tw:text-xs tw:text-gray-200">{resultsLine(winner)}</span>
                <span className="tw:text-xs tw:text-gray-200">
                    {confidence ? `Confidence that it is the best of its ad sets: ${confidence}. ` : ''}
                    {winner.adSets > 1 ? `Leads in ${winner.adSets} ad sets.` : ''}
                </span>
                {stateLabel && <span className="tw:text-xs tw:font-semibold">{stateLabel}</span>}
                {dropped && (
                    <span className="tw:text-xs tw:text-yellow-300" role="status">
                        {dropReasonText(winner.dropReason)}
                    </span>
                )}
                {notOffered && <span className="tw:text-xs tw:text-yellow-300">{notOfferedText(stateNote)}</span>}
            </div>
            {key && (
                <div className="tw:flex tw:shrink-0 tw:gap-1">
                    {winner.state === 'excluded' && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={changing}
                            aria-label={`Restore: ${label}`}
                            onClick={() => decide(key, null)}
                        >
                            Restore
                        </Button>
                    )}
                    {winner.state === 'proposed' && mode === 'manual' && (
                        <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            disabled={changing}
                            aria-label={`Approve: ${label}`}
                            onClick={() => decide(key, 'APPROVED')}
                        >
                            Approve
                        </Button>
                    )}
                    {(winner.state === 'used' || winner.state === 'proposed') && (
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={changing}
                            aria-label={`Exclude: ${label}`}
                            onClick={() => decide(key, 'EXCLUDED')}
                        >
                            Exclude
                        </Button>
                    )}
                </div>
            )}
        </li>
    )
}

const OtherRow = ({ other }: { other: AdHistoryOther }) => {
    const lines = copyLinesOf(other)
    return (
        <li className="tw:flex tw:flex-col tw:gap-0.5 tw:px-3 tw:py-2" data-testid="ad-history-other">
            {lines.map((line, index) => (
                <span key={index} className="tw:text-sm tw:break-words">
                    {line}
                </span>
            ))}
            <span className="tw:text-xs tw:text-gray-200">{resultsLine(other)}</span>
            {otherReasonTexts(other).map((text, index) => (
                <span key={index} className="tw:text-xs tw:text-yellow-300">
                    {text}
                </span>
            ))}
        </li>
    )
}

interface PurposeChoiceProps {
    purpose: GenerationBasisPurpose
    onChange: (purpose: GenerationBasisPurpose) => void
}

const PurposeChoice = ({ purpose, onChange }: PurposeChoiceProps) => (
    <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2" role="group" aria-label="Used for">
        <span className="tw:text-xs tw:text-gray-200">Used for</span>
        {PURPOSES.map((option) => (
            <Button
                key={option.value}
                type="button"
                size="sm"
                variant={purpose === option.value ? 'default' : 'secondary'}
                aria-pressed={purpose === option.value}
                onClick={() => onChange(option.value)}
            >
                {option.label}
            </Button>
        ))}
    </div>
)

const WinnersSection = ({ controller }: { controller: AdHistoryController }) => {
    const { winners, winnersLoading, changing, setMode } = controller
    if (winnersLoading || !winners) {
        return (
            <div className="tw:flex tw:items-center tw:gap-3" role="status">
                <CubeLoader size="sm" />
                <span className="tw:text-sm">Loading winning copy…</span>
            </div>
        )
    }
    if (!winners.enabled) return null

    const manual = winners.mode !== 'automated'
    const note = winnersStateNote(winners)
    const mode = manual ? 'manual' : 'automated'
    const modeKnown = winners.mode === 'manual' || winners.mode === 'automated'
    const hasWinners = winners.winners.length > 0

    return (
        <section className="tw:flex tw:flex-col tw:gap-2" aria-label="Winning copy" data-testid="ad-history-winners">
            <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2">
                <div className="tw:flex tw:flex-col">
                    <span className="tw:text-sm tw:font-semibold">Winning copy</span>
                    {modeKnown && <span className="tw:text-xs tw:text-gray-200">{MODE_TEXT[mode]}</span>}
                </div>
                {modeKnown && winners.automatedAllowed && (
                    <div className="tw:flex tw:gap-1" role="group" aria-label="Winning copy mode">
                        <Button
                            type="button"
                            size="sm"
                            variant={manual ? 'default' : 'secondary'}
                            aria-pressed={manual}
                            disabled={changing}
                            onClick={() => !manual && setMode('manual')}
                        >
                            Manual
                        </Button>
                        <Button
                            type="button"
                            size="sm"
                            variant={manual ? 'secondary' : 'default'}
                            aria-pressed={!manual}
                            disabled={changing}
                            onClick={() => manual && setMode('automated')}
                        >
                            Automated
                        </Button>
                    </div>
                )}
            </div>

            {note && (
                <p className="tw:m-0 tw:text-sm tw:text-yellow-300" role="status">
                    {note}
                </p>
            )}

            {hasWinners ? (
                <ul className="tw:m-0 tw:flex tw:list-none tw:flex-col tw:divide-y tw:rounded tw:border tw:!p-0">
                    {winners.winners.map((winner, index) => (
                        <WinnerRow
                            key={winner.key ?? `dropped-${index}`}
                            winner={winner}
                            controller={controller}
                            mode={winners.mode}
                            stateNote={winners}
                        />
                    ))}
                </ul>
            ) : (
                !note && (
                    <p className="tw:m-0 tw:text-sm tw:text-gray-200">
                        No winners yet. A copy leads only when it has clearly done better than the other copies in its
                        ad set; what each copy still needs is listed below.
                    </p>
                )
            )}

            {winners.others.length > 0 && (
                <div className="tw:flex tw:flex-col tw:gap-1" data-testid="ad-history-others">
                    <span className="tw:text-xs tw:font-semibold tw:text-gray-200">Not winners yet</span>
                    <ul className="tw:m-0 tw:flex tw:list-none tw:flex-col tw:divide-y tw:rounded tw:border tw:!p-0">
                        {winners.others.map((other, index) => (
                            <OtherRow key={index} other={other} />
                        ))}
                    </ul>
                </div>
            )}
        </section>
    )
}

/**
 * What the licence's own ads taught: how far the daily read of their results has got, the winning ad copy with the
 * evidence for it, what generation does with each winner (approve, exclude, manual or automated), and the copy that
 * does not lead yet with what it is missing.
 */
const WhatWorkedPanel = ({ campaignId }: { campaignId: string | null | undefined }) => {
    const [purpose, setPurpose] = useState<GenerationBasisPurpose>('PERSONAS')
    const controller = useAdHistory({ campaignId, purpose })
    const { loading, error, status, message, retry, clearMessage } = controller

    let body: React.ReactNode
    if (loading) {
        body = (
            <div className="tw:flex tw:items-center tw:gap-3" role="status" data-testid="ad-history-loading">
                <CubeLoader size="sm" />
                <span className="tw:text-sm">Loading what worked…</span>
            </div>
        )
    } else if (error === 'forbidden') {
        body = (
            <p className="tw:m-0 tw:text-sm tw:text-red-300" role="alert">
                {AD_HISTORY_FORBIDDEN_MESSAGE}
            </p>
        )
    } else if (error) {
        body = (
            <div className="tw:flex tw:flex-col tw:items-start tw:gap-2">
                <p className="tw:m-0 tw:text-sm tw:text-red-300" role="alert">
                    {AD_HISTORY_UNAVAILABLE_MESSAGE}
                </p>
                <Button type="button" variant="secondary" size="sm" onClick={() => retry()}>
                    Try again
                </Button>
            </div>
        )
    } else if (status && !status.enabled) {
        body = <p className="tw:m-0 tw:text-sm tw:text-gray-200">{AD_HISTORY_OFF_MESSAGE}</p>
    } else {
        body = (
            <>
                <StatusSection controller={controller} />
                <PurposeChoice
                    purpose={purpose}
                    onChange={(next) => {
                        clearMessage()
                        setPurpose(next)
                    }}
                />
                <WinnersSection controller={controller} />
                {message && (
                    <p className="tw:m-0 tw:text-sm tw:text-yellow-300" role="status">
                        {message}
                    </p>
                )}
            </>
        )
    }

    return (
        <div className="tw:flex tw:flex-col tw:gap-4" data-testid="what-worked-panel">
            <div className="tw:flex tw:flex-col tw:gap-1">
                <h1 className="tw:m-0 tw:text-xl tw:font-semibold">What worked</h1>
                <p className="tw:m-0 tw:text-sm tw:text-gray-200">
                    The ad copy from your own ads that did best, as submitted. Winning copy is given to the AI as data
                    about your past ads, to guide the angle and tone of new audiences and image and video prompts.
                </p>
            </div>
            {body}
        </div>
    )
}

export default WhatWorkedPanel
